import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Clock3 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ThreeDots } from "react-loader-spinner";
import Webcam from "react-webcam";
import { API_BASE_URL } from "../../config";

// Define types for the answers
type SectionAnswers = {
  [key: string]: string;
};

type SelectedAnswers = {
  aptitude_A: SectionAnswers;
  aptitude_B: SectionAnswers;
  aptitude_C: SectionAnswers;
  aptitude_D: SectionAnswers;
};

type CorrectAnswers = {
  aptitude_A: { [key: string]: string | string[] };
  aptitude_B: { [key: string]: string | string[] };
  aptitude_C: { [key: string]: string | string[] };
  aptitude_D: { [key: string]: string | string[] };
};

const AptitudeTest: React.FC = () => {
  const navigate = useNavigate();
  const webcamRef = React.useRef<Webcam>(null);
  // Get remaining time from previous page and add it to initial time
  const previousTimeStr = localStorage.getItem("remainingTime");
  const previousTime = previousTimeStr ? parseInt(previousTimeStr, 10) : 0;
  const [timeLeft, setTimeLeft] = useState(10 * 60 + previousTime); // 14 minutes + remaining time
  const [submitted, setSubmitted] = useState(false);
  const [selectedAnswers, setSelectedAnswers] = useState<SelectedAnswers>({
    aptitude_A: {},
    aptitude_B: {},
    aptitude_C: {},
    aptitude_D: {},
  });
  const questionId = localStorage.getItem("questionId");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [warningCount, setWarningCount] = useState(() => {
    // Get existing warning count from sessionStorage or start from 0
    const existingCount = sessionStorage.getItem("warningCount");
    return existingCount ? parseInt(existingCount, 10) : 0;
  });

  // Function to update warning count on server
  const updateWarningCount = async (count: number) => {
    try {
      const warningId = sessionStorage.getItem("warningId");
      if (!warningId) {
        console.warn("Warning ID not found in session");
        return;
      }

      const response = await fetch(
        `${API_BASE_URL}/warning/update/${warningId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            warningCount: count.toString(),
          }),
        },
      );

      if (!response.ok) {
        throw new Error(`Failed to update warning count: ${response.status}`);
      }

      console.log(`Warning count updated to ${count}`);
    } catch (error) {
      console.error("Error updating warning count:", error);
    }
  };

  const lastWarningTimeRef = React.useRef<number>(0);

  // Function to handle warning
  const handleWarning = useCallback(() => {
    const now = Date.now();
    if (now - lastWarningTimeRef.current < 2000) {
      return;
    }
    lastWarningTimeRef.current = now;

    setWarningCount((prev) => {
      const newCount = prev + 1;
      // Update warning count on server
      updateWarningCount(newCount);
      // Save warning count to sessionStorage for continuity across pages
      sessionStorage.setItem("warningCount", newCount.toString());
      return newCount;
    });
    setShowWarningModal(true);
  }, []);

  // Add check for questionId
  useEffect(() => {
    // Prevent going back
    window.history.pushState(null, "", window.location.href);
    window.onpopstate = function () {
      window.history.pushState(null, "", window.location.href);
    };

    // Handle page reload/close
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);

    // Prevent tab switching
    const handleVisibilityChange = () => {
      if (document.hidden) {
        handleWarning();
        // Force focus back to the test window
        window.focus();
      }
    };

    const handleBlur = () => {
      handleWarning();
      // Force focus back to the test window
      window.focus();
    };

    // Add event listeners for tab switching prevention
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("blur", handleBlur);

    if (!questionId) {
      navigate("/registration");
      return;
    }

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("blur", handleBlur);
    };
  }, [navigate, questionId, handleWarning]);

  const correctAnswers = useMemo<CorrectAnswers>(
    () => ({
      // Aptitude Test 1: Calculation
      aptitude_A: {
        question_b: ["20", "twenty"], // 82 - 20 = 62
        question_h: ["3", "three"], // 33 ÷ 11 = 3
      },

      // Aptitude Test 2: Matching Blocks
      aptitude_B: {
        match_q1: "B",
        match_q2: "A",
        match_q3: "D",
      },

      // Aptitude Test 3: IQ Quiz
      aptitude_C: {
        iq_q1: "13", // Triangle count
        iq_q2: "1000", // 1 kilometer = 1000 meters
        iq_q5: "9", // Shape pattern
      },

      // Aptitude Test 4: Japanese Language
      aptitude_D: {
        A: "25", // お
        B: "27", // ケ
        C: "23", // 三
        D: "39", // め
        E: "16", // 上
        F: "28", // ユ
        G: "32", // わ
        H: "14", // 九
        I: "3", // シ
        J: "35", // 犬
        K: "11", // さ
        L: "41", // ヤ
      },
    }),
    [],
  );

  // Clear the remaining time from localStorage after using it
  useEffect(() => {
    localStorage.removeItem("remainingTime");
  }, []);

  const handleInputChange = (
    section: keyof SelectedAnswers,
    questionId: string,
    value: string,
  ) => {
    setSelectedAnswers((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [questionId]: value,
      },
    }));
  };

  const getFillInBlankMarks = (
    selectedAnswer: string,
    correctAnswer: string | string[],
    isTwoAnswerQuestion: boolean
  ): number => {
    const selected = (selectedAnswer || "").trim().toLowerCase();
    if (!selected) return 0;

    const correctAnswersList = Array.isArray(correctAnswer)
      ? correctAnswer
      : [correctAnswer];

    if (isTwoAnswerQuestion) {
      const blank1Answers = new Set<string>();
      const blank2Answers = new Set<string>();

      correctAnswersList.forEach((ans) => {
        const splitParts = ans.split(/\s*,\s*|\s+and\s+|\s*&\s*|\s*\/\s*/i);
        if (splitParts.length > 1) {
          const p1 = splitParts[0].trim().toLowerCase();
          const p2 = splitParts[1].trim().toLowerCase();
          if (p1) blank1Answers.add(p1);
          if (p2) blank2Answers.add(p2);
        }
      });

      let matchedBlank1 = false;
      let matchedBlank2 = false;

      blank1Answers.forEach((b1) => {
        const regex = new RegExp(`\\b${b1}\\b`, "i");
        if (regex.test(selected) || selected === b1) {
          matchedBlank1 = true;
        }
      });

      blank2Answers.forEach((b2) => {
        const regex = new RegExp(`\\b${b2}\\b`, "i");
        if (regex.test(selected) || selected === b2) {
          matchedBlank2 = true;
        }
      });

      if (matchedBlank1 && matchedBlank2) {
        return 1;
      } else if (matchedBlank1 || matchedBlank2) {
        return 0.5;
      }
      return 0;
    } else {
      // Standard single answer
      const isCorrect = correctAnswersList.some(
        (correct) => correct.trim().toLowerCase() === selected
      );
      return isCorrect ? 1 : 0;
    }
  };

  const calculateSectionScore = useCallback(
    (
      section: keyof CorrectAnswers,
      answers: SectionAnswers,
      maxMarks: number,
      questionCount: number,
    ) => {
      let correctAnswersCount = 0;
      Object.entries(answers).forEach(([questionId, selectedAnswer]) => {
        const correct = correctAnswers[section][questionId];
        const selected = (selectedAnswer || "").trim().toLowerCase();

        if (correct) {
          const correctList = Array.isArray(correct) ? correct : [correct];
          const hasTwoAnswers = correctList.some(ans => ans.split(/\s*,\s*|\s+and\s+|\s*&\s*|\s*\/\s*/i).length > 1);
          correctAnswersCount += getFillInBlankMarks(selected, correct, hasTwoAnswers);
        }
      });

      // Calculate marks based on section weights
      const marksPerQuestion = maxMarks / questionCount;
      return correctAnswersCount * marksPerQuestion;
    },
    [correctAnswers],
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setShowModal(true);
  };

  const confirmSubmit = useCallback(async () => {
    setIsSubmitting(true);
    setSubmitted(true);

    // Save remaining time for next page (if needed)
    localStorage.setItem("remainingTime", timeLeft.toString());

    try {
      // Calculate scores for each section with their respective weights
      const aptitudeAScore = calculateSectionScore(
        "aptitude_A",
        selectedAnswers.aptitude_A,
        2,
        2,
      );

      const aptitudeBScore = calculateSectionScore(
        "aptitude_B",
        selectedAnswers.aptitude_B,
        2,
        3,
      );

      const aptitudeCScore = calculateSectionScore(
        "aptitude_C",
        selectedAnswers.aptitude_C,
        2,
        3,
      );

      const aptitudeDScore = calculateSectionScore(
        "aptitude_D",
        selectedAnswers.aptitude_D,
        10,
        10,
      );

      const scores = {
        aptitude_A: {
          correctAnswer: correctAnswers.aptitude_A,
          selectedAnswer: selectedAnswers.aptitude_A,
          marks: aptitudeAScore,
          questionType: "calculation",
        },
        aptitude_B: {
          correctAnswer: correctAnswers.aptitude_B,
          selectedAnswer: selectedAnswers.aptitude_B,
          marks: aptitudeBScore,
          questionType: "matching",
        },
        aptitude_C: {
          correctAnswer: correctAnswers.aptitude_C,
          selectedAnswer: selectedAnswers.aptitude_C,
          marks: aptitudeCScore,
          questionType: "iq",
        },
        aptitude_D: {
          correctAnswer: correctAnswers.aptitude_D,
          selectedAnswer: selectedAnswers.aptitude_D,
          marks: aptitudeDScore,
          questionType: "japanese",
        },
      };

      const response = await fetch(
        `${API_BASE_URL}/screening-questions/${questionId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(scores),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to submit answers");
      }

      console.log("Answers submitted successfully");

      // Navigate to listening-instructions page after successful submission
      navigate("/completion");
    } catch (error) {
      console.error("Error submitting answers:", error);
      alert("Submission failed. Please check your internet connection and try clicking Confirm again.");
      setSubmitted(false);
    } finally {
      setIsSubmitting(false);
      setShowModal(false);
    }
  }, [
    timeLeft,
    selectedAnswers,
    correctAnswers,
    questionId,
    navigate,
    calculateSectionScore,
    setIsSubmitting,
    setSubmitted,
    setShowModal,
  ]);

  useEffect(() => {
    if (timeLeft > 0 && !submitted) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0 && !submitted) {
      // Auto-submit when timer reaches zero
      setSubmitted(true);
      confirmSubmit();
    }
  }, [timeLeft, submitted, confirmSubmit]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  // Add this useEffect at the top of your other useEffects
  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  return (
    <form onSubmit={handleSubmit} autoComplete="off">
      <div className="p-4 sm:p-8">
        {/* Webcam */}
        <div className="fixed bottom-5 right-5 z-20">
          <div className="bg-white/90 border-2 border-red-200 p-1 rounded-xl custom-shadow">
            <Webcam
              audio={false}
              ref={webcamRef}
              screenshotFormat="image/jpeg"
              videoConstraints={{
                width: 150,
                height: 100,
                facingMode: "user",
              }}
              className="rounded-lg"
            />
          </div>
        </div>
        {/* Timer */}
        <div className="bg-white/90 border-2 border-red-200 p-3 rounded-xl custom-shadow timer-container fixed top-5 right-5 z-20">
          <div className="flex gap-2 items-center">
            <div className="bg-gradient-to-r p-2 rounded-full from-red-500 to-pink-600">
              <Clock3 className="text-white" />
            </div>
            <div>
              <p className="text-gray-500 text-xs font-medium">
                Time Remaining
              </p>
              <p id="timerDisplay" className="text-gray-800 text-lg font-bold">
                {formatTime(timeLeft)}
              </p>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-4xl mx-auto">
          {/* Header */}

          <div
            className="bg-white p-8 rounded-3xl shadow-lg mb-10 overflow-hidden relative
        "
          >
            <div className="bg-red-50 h-40 rounded-full w-40 -mr-12 -mt-12 absolute opacity-70 right-0 top-0"></div>
            <div className="bg-red-50 h-32 rounded-full w-32 -mb-8 -ml-8 absolute bottom-0 left-0 opacity-70"></div>
            <div className="relative z-10">
              <h1 className="bg-clip-text bg-gradient-to-r text-4xl text-transparent font-bold from-red-600 mb-5 to-pink-600">
                Aptitude Test
              </h1>
              <div className="bg-gradient-to-r h-1.5 rounded-full w-24 from-red-500 mb-6 to-pink-500"></div>
            </div>
          </div>

          {/* Aptitude Test Sections */}
          <div className="bg-white/80 border border-red-100 rounded-3xl shadow-xl py-4 sm:py-8">
            {/* Calculation Section */}
            <div className="border-b border-gray-300 p-4 sm:p-8">
              <div className="flex justify-center items-center mb-8">
                <h2 className="bg-clip-text bg-gradient-to-r text-3xl text-center text-transparent font-bold from-red-600 to-pink-600">
                  Aptitude Test 1 : Calculation
                </h2>
              </div>
              <h2 className="text-sm sm:text-base md:text-lg font-semibold mb-3 sm:mb-4 md:mb-6 lg:mb-8">
                Calculate and enter the missing answer in the box.
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse font-semibold text-xs sm:text-sm md:text-base">
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 p-2 sm:p-3 md:p-4">
                        <div className="flex flex-wrap items-center gap-1 sm:gap-2 text-sm sm:text-base md:text-lg">
                          <span>a)</span>
                          <span>82 -</span>
                          <input
                            type="text"
                            name="question_b"
                            className="border border-gray-300 rounded p-1 sm:p-2 w-12 sm:w-14 md:w-16 focus:ring-2 focus:ring-blue-500 focus:outline-none text-center"
                            onChange={(e) =>
                              handleInputChange(
                                "aptitude_A",
                                "question_b",
                                e.target.value,
                              )
                            }
                          />
                          <span>= 62</span>
                        </div>
                      </td>
                      <td className="border border-gray-300 p-2 sm:p-3 md:p-4">
                        <div className="flex flex-wrap items-center gap-1 sm:gap-2 text-sm sm:text-base md:text-lg">
                          <span>b)</span>
                          <span>33 ÷ 11 =</span>
                          <input
                            type="text"
                            name="question_h"
                            className="border border-gray-300 rounded p-1 sm:p-2 w-12 sm:w-14 md:w-16 focus:ring-2 focus:ring-blue-500 focus:outline-none text-center"
                            onChange={(e) =>
                              handleInputChange(
                                "aptitude_A",
                                "question_h",
                                e.target.value,
                              )
                            }
                          />
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Matching Blocks Section */}
            <div className="border-b border-gray-300 p-4 sm:p-8">
              <div className="flex justify-center items-center mb-8">
                <h2 className="bg-clip-text bg-gradient-to-r text-3xl text-center text-transparent font-bold from-red-600 to-pink-600">
                  Aptitude Test 2 : Matching the Blocks
                </h2>
              </div>
              <h2 className="text-base sm:text-lg font-semibold mb-4 sm:mb-8">
                Find the Same Blocks and enter the answer in the box (A, B, C,
                D).
              </h2>
              <div className="w-full overflow-x-auto">
                <table className="w-full min-w-[600px] sm:min-w-[800px] border-collapse font-semibold text-sm sm:text-base">
                  <thead>
                    <tr className="bg-gray-200">
                      <th className="p-4 border border-gray-300 text-left w-12">
                        No.
                      </th>
                      <th className="p-4 border border-gray-300 text-left flex-1">
                        Question
                      </th>
                      <th className="p-4 border border-gray-300 text-center w-32">
                        Option A
                      </th>
                      <th className="p-4 border border-gray-300 text-center w-32">
                        Option B
                      </th>
                      <th className="p-4 border border-gray-300 text-center w-32">
                        Option C
                      </th>
                      <th className="p-4 border border-gray-300 text-center w-32">
                        Option D
                      </th>
                      <th className="p-4 border border-gray-300 text-center w-32">
                        Enter Your Answer
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Example Row */}
                    <tr className="hover:bg-gray-50">
                      <td className="p-4 border border-gray-300 text-center">
                        1
                      </td>
                      <td className="p-2 border border-gray-300">
                        <div className="flex flex-col items-center gap-1">
                          <img
                            src="https://res.cloudinary.com/dureqzdco/image/upload/v1742615655/Q1-A4_kfo2qw.svg"
                            alt="Question 1"
                            className="w-30 h-30 object-cover"
                          />
                        </div>
                      </td>
                      <td className="p-2 border border-gray-300">
                        <div className="flex flex-col items-center gap-1">
                          <img
                            src="https://res.cloudinary.com/dureqzdco/image/upload/v1742615655/Q1-A1_rtg7qo.svg"
                            alt="Option A"
                            className="w-30 h-30 object-cover"
                          />
                        </div>
                      </td>
                      <td className="p-2 border border-gray-300">
                        <div className="flex flex-col items-center gap-1">
                          <img
                            src="https://res.cloudinary.com/dureqzdco/image/upload/v1742615655/Q1-A2_erink8.svg"
                            alt="Option B"
                            className="w-30 h-30 object-cover"
                          />
                        </div>
                      </td>
                      <td className="p-2 border border-gray-300">
                        <div className="flex flex-col items-center gap-1">
                          <img
                            src="https://res.cloudinary.com/dureqzdco/image/upload/v1742615656/Q1-A3_fazwne.svg"
                            alt="Option C"
                            className="w-30 h-30 object-cover"
                          />
                        </div>
                      </td>
                      <td className="p-2 border border-gray-300">
                        <div className="flex flex-col items-center gap-1">
                          <img
                            src="https://res.cloudinary.com/dureqzdco/image/upload/v1742615655/Q1-1_i2l0ep.svg"
                            alt="Option D"
                            className="w-30 h-30 object-cover"
                          />
                        </div>
                      </td>
                      <td className="p-4 border border-gray-300">
                        <div className="flex justify-center">
                          <input
                            type="text"
                            name="match_q1"
                            className="border border-gray-300 rounded p-2 w-16 text-center focus:ring-2 focus:ring-blue-500 focus:outline-none"
                            maxLength={1}
                            pattern="[ABCDabcd]"
                            style={{ textTransform: "uppercase" }}
                            onChange={(e) =>
                              handleInputChange(
                                "aptitude_B",
                                "match_q1",
                                e.target.value.toUpperCase(),
                              )
                            }
                          />
                        </div>
                      </td>
                    </tr>
                    {/* Question 2 */}
                    <tr className="hover:bg-gray-50">
                      <td className="p-4 border border-gray-300 text-center">
                        2
                      </td>
                      <td className="p-2 border border-gray-300">
                        <div className="flex flex-col items-center gap-1">
                          <img
                            src="https://res.cloudinary.com/dureqzdco/image/upload/v1742615657/Q2-2_qekdww.svg"
                            alt="Question 2"
                            className="w-30 h-30 object-cover"
                          />
                        </div>
                      </td>
                      <td className="p-2 border border-gray-300">
                        <div className="flex flex-col items-center gap-1">
                          <img
                            src="https://res.cloudinary.com/dureqzdco/image/upload/v1742615655/Q2-A1_wzqvqj.svg"
                            alt="Option A"
                            className="w-30 h-30 object-cover"
                          />
                        </div>
                      </td>
                      <td className="p-2 border border-gray-300">
                        <div className="flex flex-col items-center gap-1">
                          <img
                            src="https://res.cloudinary.com/dureqzdco/image/upload/v1742615655/Q2-A2_tgxknk.svg"
                            alt="Option B"
                            className="w-30 h-30 object-cover"
                          />
                        </div>
                      </td>
                      <td className="p-2 border border-gray-300">
                        <div className="flex flex-col items-center gap-1">
                          <img
                            src="https://res.cloudinary.com/dureqzdco/image/upload/v1742615657/Q2-A3_aodbbs.svg"
                            alt="Option C"
                            className="w-30 h-30 object-cover"
                          />
                        </div>
                      </td>
                      <td className="p-2 border border-gray-300">
                        <div className="flex flex-col items-center gap-1">
                          <img
                            src="https://res.cloudinary.com/dureqzdco/image/upload/v1742615657/Q2-A4_zjxqbc.svg"
                            alt="Option D"
                            className="w-30 h-30 object-cover"
                          />
                        </div>
                      </td>
                      <td className="p-4 border border-gray-300">
                        <div className="flex justify-center">
                          <input
                            type="text"
                            name="match_q2"
                            className="border border-gray-300 rounded p-2 w-16 text-center focus:ring-2 focus:ring-blue-500 focus:outline-none"
                            maxLength={1}
                            pattern="[ABCDabcd]"
                            style={{ textTransform: "uppercase" }}
                            onChange={(e) =>
                              handleInputChange(
                                "aptitude_B",
                                "match_q2",
                                e.target.value.toUpperCase(),
                              )
                            }
                          />
                        </div>
                      </td>
                    </tr>
                    {/* Question 3 */}
                    <tr className="hover:bg-gray-50">
                      <td className="p-4 border border-gray-300 text-center">
                        3
                      </td>
                      <td className="p-2 border border-gray-300">
                        <div className="flex flex-col items-center gap-1">
                          <img
                            src="https://res.cloudinary.com/dureqzdco/image/upload/v1742615657/Q3-3_jwuauq.svg"
                            alt="Question 3"
                            className="w-30 h-30 object-cover"
                          />
                        </div>
                      </td>
                      <td className="p-2 border border-gray-300">
                        <div className="flex flex-col items-center gap-1">
                          <img
                            src="https://res.cloudinary.com/dureqzdco/image/upload/v1742615657/Q3-A1_p6ilz9.svg"
                            alt="Option A"
                            className="w-30 h-30 object-cover"
                          />
                        </div>
                      </td>
                      <td className="p-2 border border-gray-300">
                        <div className="flex flex-col items-center gap-1">
                          <img
                            src="https://res.cloudinary.com/dureqzdco/image/upload/v1742615658/Q3-A2_zhjkfz.svg"
                            alt="Option B"
                            className="w-30 h-30 object-cover"
                          />
                        </div>
                      </td>
                      <td className="p-2 border border-gray-300">
                        <div className="flex flex-col items-center gap-1">
                          <img
                            src="https://res.cloudinary.com/dureqzdco/image/upload/v1742615658/Q3-A3_aq2qol.svg"
                            alt="Option C"
                            className="w-30 h-30 object-cover"
                          />
                        </div>
                      </td>
                      <td className="p-2 border border-gray-300">
                        <div className="flex flex-col items-center gap-1">
                          <img
                            src="https://res.cloudinary.com/dureqzdco/image/upload/v1742615658/Q3-A4_cl30z4.svg"
                            alt="Option D"
                            className="w-30 h-30 object-cover"
                          />
                        </div>
                      </td>
                      <td className="p-4 border border-gray-300">
                        <div className="flex justify-center">
                          <input
                            type="text"
                            name="match_q3"
                            className="border border-gray-300 rounded p-2 w-16 text-center focus:ring-2 focus:ring-blue-500 focus:outline-none"
                            maxLength={1}
                            pattern="[ABCDabcd]"
                            style={{ textTransform: "uppercase" }}
                            onChange={(e) =>
                              handleInputChange(
                                "aptitude_B",
                                "match_q3",
                                e.target.value.toUpperCase(),
                              )
                            }
                          />
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* IQ Quiz Section */}
            <div className="border-b border-gray-300 p-4 sm:p-8">
              <div className="flex justify-center items-center mb-8">
                <h2 className="bg-clip-text bg-gradient-to-r text-3xl text-center text-transparent font-bold from-red-600 to-pink-600">
                  Aptitude Test 3 : IQ Quiz
                </h2>
              </div>
              <h2 className="text-base sm:text-lg font-semibold mb-4 sm:mb-8">
                Read the Question and Choose Appropriate Answer.
              </h2>
              <table className="w-full border-collapse font-semibold text-sm sm:text-base">
                <tbody>
                  {/* Example Question */}
                  <tr>
                    <td className="border border-gray-300 p-2 sm:p-4">
                      <div className="flex items-center gap-2 text-lg">
                        <span>1)</span>
                        <span>How many triangles do you see?</span>
                      </div>
                      <div className="flex pl-5 py-4">
                        <img
                          src="https://res.cloudinary.com/dureqzdco/image/upload/v1742616626/triangle_fm1aud.svg"
                          alt="triangles"
                          className="w-[30%]"
                        />
                      </div>
                      <div className="flex flex-col sm:flex-row mt-4 gap-2">
                        <label className="flex items-center mx-2 sm:mx-4 bg-gray-100 p-2 rounded w-full sm:w-1/2 cursor-pointer hover:bg-blue-100">
                          <input
                            type="radio"
                            name="iq_q1"
                            value="9"
                            onChange={(e) =>
                              handleInputChange(
                                "aptitude_C",
                                "iq_q1",
                                e.target.value,
                              )
                            }
                            className="mr-2"
                          />
                          <span>9</span>
                        </label>
                        <label className="flex items-center mx-2 sm:mx-4 bg-gray-100 p-2 rounded w-full sm:w-1/2 cursor-pointer hover:bg-blue-100">
                          <input
                            type="radio"
                            name="iq_q1"
                            value="13"
                            onChange={(e) =>
                              handleInputChange(
                                "aptitude_C",
                                "iq_q1",
                                e.target.value,
                              )
                            }
                            className="mr-2"
                          />
                          <span>13</span>
                        </label>
                      </div>
                      <div className="flex flex-col sm:flex-row mt-2 gap-2">
                        <label className="flex items-center mx-2 sm:mx-4 bg-gray-100 p-2 rounded w-full sm:w-1/2 cursor-pointer hover:bg-blue-100">
                          <input
                            type="radio"
                            name="iq_q1"
                            value="17"
                            onChange={(e) =>
                              handleInputChange(
                                "aptitude_C",
                                "iq_q1",
                                e.target.value,
                              )
                            }
                            className="mr-2"
                          />
                          <span>17</span>
                        </label>
                        <label className="flex items-center mx-2 sm:mx-4 bg-gray-100 p-2 rounded w-full sm:w-1/2 cursor-pointer hover:bg-blue-100">
                          <input
                            type="radio"
                            name="iq_q1"
                            value="15"
                            onChange={(e) =>
                              handleInputChange(
                                "aptitude_C",
                                "iq_q1",
                                e.target.value,
                              )
                            }
                            className="mr-2"
                          />
                          <span>15</span>
                        </label>
                      </div>
                    </td>
                  </tr>
                  {/* Question 2 - Letter Box */}
                  <tr className="w-full">
                    <td className="border border-gray-300 p-2 sm:p-4">
                      <div className="flex items-center gap-2 text-lg">
                        <span>2)</span>
                        <span>1 kilometer = _______ meters</span>
                      </div>
                      <div>
                        <div className="flex flex-col sm:flex-row mt-4 gap-2">
                          <label className="flex items-center mx-2 sm:mx-4 bg-gray-100 p-2 rounded w-full sm:w-1/2 cursor-pointer hover:bg-blue-100">
                            <input
                              type="radio"
                              name="iq_q2"
                              value="1000"
                              onChange={(e) =>
                                handleInputChange(
                                  "aptitude_C",
                                  "iq_q2",
                                  e.target.value,
                                )
                              }
                              className="mr-2"
                            />
                            <span>1000</span>
                          </label>
                          <label className="flex items-center mx-2 sm:mx-4 bg-gray-100 p-2 rounded w-full sm:w-1/2 cursor-pointer hover:bg-blue-100">
                            <input
                              type="radio"
                              name="iq_q2"
                              value="10000"
                              onChange={(e) =>
                                handleInputChange(
                                  "aptitude_C",
                                  "iq_q2",
                                  e.target.value,
                                )
                              }
                              className="mr-2"
                            />
                            <span>10,000</span>
                          </label>
                        </div>
                        <div className="flex flex-col sm:flex-row mt-2 gap-2">
                          <label className="flex items-center mx-2 sm:mx-4 bg-gray-100 p-2 rounded w-full sm:w-1/2 cursor-pointer hover:bg-blue-100">
                            <input
                              type="radio"
                              name="iq_q2"
                              value="100"
                              onChange={(e) =>
                                handleInputChange(
                                  "aptitude_C",
                                  "iq_q2",
                                  e.target.value,
                                )
                              }
                              className="mr-2"
                            />
                            <span>100</span>
                          </label>
                          <label className="flex items-center mx-2 sm:mx-4 bg-gray-100 p-2 rounded w-full sm:w-1/2 cursor-pointer hover:bg-blue-100">
                            <input
                              type="radio"
                              name="iq_q2"
                              value="500"
                              onChange={(e) =>
                                handleInputChange(
                                  "aptitude_C",
                                  "iq_q2",
                                  e.target.value,
                                )
                              }
                              className="mr-2"
                            />
                            <span>500</span>
                          </label>
                        </div>
                      </div>
                    </td>
                  </tr>

                  <tr className="w-full">
                    <td className="border border-gray-300 p-2 sm:p-4">
                      <div className="flex items-center gap-2 text-lg">
                        <span>3)</span>
                        <span>
                          If a vehicle number is 7896, which is the largest
                          digit in the number?
                        </span>
                      </div>
                      <div>
                        <div className="flex flex-col sm:flex-row mt-4 gap-2">
                          <label className="flex items-center mx-2 sm:mx-4 bg-gray-100 p-2 rounded w-full sm:w-1/2 cursor-pointer hover:bg-blue-100">
                            <input
                              type="radio"
                              name="iq_q5"
                              value="7"
                              onChange={(e) =>
                                handleInputChange(
                                  "aptitude_C",
                                  "iq_q5",
                                  e.target.value,
                                )
                              }
                              className="mr-2"
                            />
                            <span>7</span>
                          </label>
                          <label className="flex items-center mx-2 sm:mx-4 bg-gray-100 p-2 rounded w-full sm:w-1/2 cursor-pointer hover:bg-blue-100">
                            <input
                              type="radio"
                              name="iq_q5"
                              value="8"
                              onChange={(e) =>
                                handleInputChange(
                                  "aptitude_C",
                                  "iq_q5",
                                  e.target.value,
                                )
                              }
                              className="mr-2"
                            />
                            <span>8</span>
                          </label>
                        </div>
                        <div className="flex flex-col sm:flex-row mt-2 gap-2">
                          <label className="flex items-center mx-2 sm:mx-4 bg-gray-100 p-2 rounded w-full sm:w-1/2 cursor-pointer hover:bg-blue-100">
                            <input
                              type="radio"
                              name="iq_q5"
                              value="9"
                              onChange={(e) =>
                                handleInputChange(
                                  "aptitude_C",
                                  "iq_q5",
                                  e.target.value,
                                )
                              }
                              className="mr-2"
                            />
                            <span>9</span>
                          </label>
                          <label className="flex items-center mx-2 sm:mx-4 bg-gray-100 p-2 rounded w-full sm:w-1/2 cursor-pointer hover:bg-blue-100">
                            <input
                              type="radio"
                              name="iq_q5"
                              value="6"
                              onChange={(e) =>
                                handleInputChange(
                                  "aptitude_C",
                                  "iq_q5",
                                  e.target.value,
                                )
                              }
                              className="mr-2"
                            />
                            <span>6</span>
                          </label>
                        </div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Japanese Language Section - Additional Questions */}
            <div className="border-b-[1px] border-gray-300 p-4 sm:p-8">
              <div className="flex justify-center items-center mb-8">
                <h2 className="bg-clip-text bg-gradient-to-r text-3xl text-center text-transparent font-bold from-red-600 to-pink-600">
                  Aptitude Test 4 : Japanese Language
                </h2>
              </div>
              <h2 className="text-base sm:text-lg font-semibold mb-4 sm:mb-8">
                A) From (1) to (42) in the box below, choose one sign which has
                the totally same shape as that of (A) to (L), respectively. You
                have 2 minutes for this.
              </h2>

              <div className="flex justify-center">
                <div className="bg-white border max-w-3xl w-full my-1 font-bold text-xl">
                  <div className="grid grid-cols-6 gap-4 py-1">
                    {/* <!-- Row 1 --> */}
                    <div className="flex flex-col items-center">
                      <div className="text-lg mb-1">EX: (1) か</div>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="text-lg mb-1">(2) タ</div>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="text-lg mb-1">(3) シ</div>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="text-lg mb-1">(4) の</div>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="text-lg mb-1">(5) 刀</div>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="text-lg mb-1">(6) 士</div>
                    </div>

                    {/* <!-- Row 2 --> */}
                    <div className="flex flex-col items-center">
                      <div className="text-lg mb-1">(7) ぬ</div>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="text-lg mb-1">(8) 大</div>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="text-lg mb-1">(9) ち</div>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="text-lg mb-1">(10) カ</div>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="text-lg mb-1">(11) さ</div>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="text-lg mb-1">(12) ト</div>
                    </div>

                    {/* <!-- Row 3 --> */}
                    <div className="flex flex-col items-center">
                      <div className="text-lg mb-1">(13) あ</div>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="text-lg mb-1">(14) 九</div>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="text-lg mb-1">(15) れ</div>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="text-lg mb-1">(16) 上</div>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="text-lg mb-1">(17) ソ</div>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="text-lg mb-1">(18) 山</div>
                    </div>

                    {/* <!-- Row 4 --> */}
                    <div className="flex flex-col items-center">
                      <div className="text-lg mb-1">(19) マ</div>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="text-lg mb-1">(20) ね</div>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="text-lg mb-1">(21) 二</div>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="text-lg mb-1">(22) ヨ</div>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="text-lg mb-1">(23) 三</div>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="text-lg mb-1">(24) コ</div>
                    </div>

                    {/* <!-- Row 5 --> */}
                    <div className="flex flex-col items-center">
                      <div className="text-lg mb-1">(25) お</div>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="text-lg mb-1">(26) ク</div>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="text-lg mb-1">(27) ケ</div>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="text-lg mb-1">(28) ユ</div>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="text-lg mb-1">(29) 下</div>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="text-lg mb-1">(30) エ</div>
                    </div>

                    {/* <!-- Row 6 --> */}
                    <div className="flex flex-col items-center">
                      <div className="text-lg mb-1">(31) ツ</div>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="text-lg mb-1">(32) わ</div>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="text-lg mb-1">(33) ア</div>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="text-lg mb-1">(34) ミ</div>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="text-lg mb-1">(35) 犬</div>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="text-lg mb-1">(36) 川</div>
                    </div>

                    {/* <!-- Row 7 --> */}
                    <div className="flex flex-col items-center">
                      <div className="text-lg mb-1">(37) 丸</div>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="text-lg mb-1">(38) 太</div>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="text-lg mb-1">(39) め</div>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="text-lg mb-1">(40) ン</div>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="text-lg mb-1">(41) ヤ</div>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="text-lg mb-1">(42) き</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white border p-6 w-full max-w-4xl">
                {/* <!-- Example Row --> */}
                <div className="flex items-center gap-2 mb-6">
                  <span className="font-bold text-xl">
                    Ex: <span className="bg-blue-200 p-1 rounded"> (1) か</span>
                  </span>
                </div>

                {/* <!-- Main Grid --> */}
                <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-4 gap-4">
                  {/* <!-- Row 1 --> */}
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-xl">(A) お</span>
                    <input
                      type="text"
                      className="w-12 h-8 border border-gray-300 rounded text-center focus:outline-none focus:ring-2 focus:ring-blue-500 font-bold text-xl"
                      onChange={(e) =>
                        handleInputChange("aptitude_D", "A", e.target.value)
                      }
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-xl">(B) ケ</span>
                    <input
                      type="text"
                      className="w-12 h-8 border border-gray-300 rounded text-center focus:outline-none focus:ring-2 focus:ring-blue-500 font-bold text-xl"
                      onChange={(e) =>
                        handleInputChange("aptitude_D", "B", e.target.value)
                      }
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-xl">(C) 三</span>
                    <input
                      type="text"
                      className="w-12 h-8 border border-gray-300 rounded text-center focus:outline-none focus:ring-2 focus:ring-blue-500 font-bold text-xl"
                      onChange={(e) =>
                        handleInputChange("aptitude_D", "C", e.target.value)
                      }
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-xl">(D) め</span>
                    <input
                      type="text"
                      className="w-12 h-8 border border-gray-300 rounded text-center focus:outline-none focus:ring-2 focus:ring-blue-500 font-bold text-xl"
                      onChange={(e) =>
                        handleInputChange("aptitude_D", "D", e.target.value)
                      }
                    />
                  </div>

                  {/* <!-- Row 2 --> */}
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-xl">(E) 上</span>
                    <input
                      type="text"
                      className="w-12 h-8 border border-gray-300 rounded text-center focus:outline-none focus:ring-2 focus:ring-blue-500 font-bold text-xl"
                      onChange={(e) =>
                        handleInputChange("aptitude_D", "E", e.target.value)
                      }
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-xl">(F) ユ</span>
                    <input
                      type="text"
                      className="w-12 h-8 border border-gray-300 rounded text-center focus:outline-none focus:ring-2 focus:ring-blue-500 font-bold text-xl"
                      onChange={(e) =>
                        handleInputChange("aptitude_D", "F", e.target.value)
                      }
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-xl">(G) わ</span>
                    <input
                      type="text"
                      className="w-12 h-8 border border-gray-300 rounded text-center focus:outline-none focus:ring-2 focus:ring-blue-500 font-bold text-xl"
                      onChange={(e) =>
                        handleInputChange("aptitude_D", "G", e.target.value)
                      }
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-xl">(H) 九</span>
                    <input
                      type="text"
                      className="w-12 h-8 border border-gray-300 rounded text-center focus:outline-none focus:ring-2 focus:ring-blue-500 font-bold text-xl"
                      onChange={(e) =>
                        handleInputChange("aptitude_D", "H", e.target.value)
                      }
                    />
                  </div>

                  {/* <!-- Row 3 --> */}
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-xl">(I) シ</span>
                    <input
                      type="text"
                      className="w-12 h-8 border border-gray-300 rounded text-center focus:outline-none focus:ring-2 focus:ring-blue-500 font-bold text-xl"
                      onChange={(e) =>
                        handleInputChange("aptitude_D", "I", e.target.value)
                      }
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-xl">(J) 犬</span>
                    <input
                      type="text"
                      className="w-12 h-8 border border-gray-300 rounded text-center focus:outline-none focus:ring-2 focus:ring-blue-500 font-bold text-xl"
                      onChange={(e) =>
                        handleInputChange("aptitude_D", "J", e.target.value)
                      }
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-xl">(K) さ</span>
                    <input
                      type="text"
                      className="w-12 h-8 border border-gray-300 rounded text-center focus:outline-none focus:ring-2 focus:ring-blue-500 font-bold text-xl"
                      onChange={(e) =>
                        handleInputChange("aptitude_D", "K", e.target.value)
                      }
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-xl">(L) ヤ</span>
                    <input
                      type="text"
                      className="w-12 h-8 border border-gray-300 rounded text-center focus:outline-none focus:ring-2 focus:ring-blue-500 font-bold text-xl"
                      onChange={(e) =>
                        handleInputChange("aptitude_D", "L", e.target.value)
                      }
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-center mb-8 mt-12">
              <button
                type="submit"
                className="bg-gradient-to-r rounded-xl shadow-lg text-white w-1/2 duration-300 focus:outline-none focus:ring-2 focus:ring-opacity-50 focus:ring-red-500 font-bold from-red-500 hover:scale-[1.02] hover:shadow-2xl px-4 py-3 to-pink-600 transform transition-all flex justify-center items-center"
                disabled={submitted || isSubmitting}
              >
                <span>Submit</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              Confirm Submission
            </h2>
            <p className="text-gray-600 mb-6">
              This is the last test of this round. Are you sure you want to
              submit?
            </p>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                disabled={isSubmitting}
                type="button"
              >
                Cancel
              </button>
              <button
                onClick={confirmSubmit}
                className="px-4 py-2 bg-gradient-to-r from-red-500 to-pink-600 text-white rounded-lg flex items-center justify-center min-w-[100px]"
                disabled={isSubmitting}
                type="button"
              >
                {isSubmitting ? (
                  <ThreeDots
                    height="24"
                    width="50"
                    color="#ffffff"
                    ariaLabel="loading"
                    wrapperClass="flex justify-center items-center"
                  />
                ) : (
                  "Confirm"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Warning Modal */}
      {showWarningModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
            <h2 className="text-2xl font-bold text-red-600 mb-4">
              Warning: Tab Switching Detected
            </h2>
            <p className="text-gray-600 mb-2">
              Please do not switch tabs or windows during the test. This may
              result in test termination.
            </p>
            <p className="text-red-500 font-semibold mb-6">
              Warning Count: {warningCount}
            </p>
            <div className="flex justify-end">
              <button
                onClick={() => {
                  setShowWarningModal(false);
                  window.focus();
                }}
                className="px-4 py-2 bg-gradient-to-r from-red-500 to-pink-600 text-white rounded-lg"
              >
                I Understand
              </button>
            </div>
          </div>
        </div>
      )}
    </form>
  );
};

export default AptitudeTest;
